import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";


import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertDocumentChecklistSchema, type DocumentChecklist, type LenderProduct } from "@shared/schema";
import { z } from "zod";

type ChecklistFormData = z.infer<typeof insertDocumentChecklistSchema>;

interface DocumentChecklistFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenderId: string;
  checklist?: DocumentChecklist & { productName?: string; productIds?: string[] };
  defaultProductId?: string;
  onChecklistCreated?: (checklist: DocumentChecklist) => void;
}

export function DocumentChecklistFormModal({
  open,
  onOpenChange,
  lenderId,
  checklist,
  defaultProductId,
  onChecklistCreated,
}: DocumentChecklistFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!checklist;

  // ISSUE-069: Multi-product selection state
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // Fetch products for this lender
  const { data: products = [] } = useQuery<LenderProduct[]>({
    queryKey: ["/api/crm/lenders", lenderId, "products"],
    enabled: open,
  });

  // Fetch product IDs for existing checklist (ISSUE-069)
  const { data: checklistData } = useQuery<DocumentChecklist & { productIds: string[] }>({
    queryKey: ["/api/crm/document-checklists", checklist?.id],
    enabled: open && isEdit && !!checklist?.id,
  });

  const defaultValues: Partial<ChecklistFormData> = {
    lenderProductId: defaultProductId || "",
    checklistName: "",
    description: "",
    isActive: 1,
  };

  const form = useForm<ChecklistFormData>({
    resolver: zodResolver(insertDocumentChecklistSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (checklist) {
        form.reset({
          lenderProductId: checklist.lenderProductId || undefined,
          checklistName: checklist.checklistName,
          description: checklist.description || "",
          isActive: checklist.isActive ?? 1,
        });

        // Set selected products from API response (ISSUE-069)
        if (checklistData?.productIds) {
          setSelectedProductIds(checklistData.productIds);
        } else if (checklist.lenderProductId) {
          // Fallback to legacy single product
          setSelectedProductIds([checklist.lenderProductId]);
        }
      } else {
        form.reset({
          ...defaultValues,
          lenderProductId: defaultProductId || products[0]?.id || "",
        });

        // Initialize with default product if provided (ISSUE-069)
        setSelectedProductIds(defaultProductId ? [defaultProductId] : []);
      }
    }
  }, [open, checklist, checklistData, products, defaultProductId, form]);

  const createMutation = useMutation({
    mutationFn: async (data: ChecklistFormData) => {
      // Send productIds array instead of single product (ISSUE-069)
      const response = await apiRequest(
        "POST",
        `/api/crm/lender-products/${data.lenderProductId || selectedProductIds[0]}/document-checklists`,
        {
          ...data,
          productIds: selectedProductIds,
        }
      );
      return response.json();
    },
    onSuccess: async (newChecklist: DocumentChecklist) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/lenders", lenderId, "document-checklists"] });
      toast({
        title: "Checklist created",
        description: "The document checklist has been created successfully.",
      });
      onOpenChange(false);
      if (onChecklistCreated) onChecklistCreated(newChecklist);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ChecklistFormData) =>
      apiRequest("PATCH", `/api/crm/document-checklists/${checklist?.id}`, {
        ...data,
        productIds: selectedProductIds, // ISSUE-069
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/lenders", lenderId, "document-checklists"] });
      toast({
        title: "Checklist updated",
        description: "The document checklist has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ChecklistFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Document Checklist" : "Add Document Checklist"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update document checklist information"
              : "Create a new document checklist for a product"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* ISSUE-069: Multi-product selection with checkboxes */}
            <FormItem>
              <FormLabel>Products *</FormLabel>
              <FormDescription>
                Select one or more products this checklist applies to
              </FormDescription>
              <div className="mt-2 space-y-2 border rounded-md p-3 max-h-48 overflow-y-auto">
                {products.length === 0 && (
                  <p className="text-sm text-muted-foreground">No products available</p>
                )}
                {products.map((product) => {
                  const isSelected = selectedProductIds.includes(product.id);
                  return (
                    <div key={product.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`product-${product.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedProductIds([...selectedProductIds, product.id]);
                          } else {
                            setSelectedProductIds(selectedProductIds.filter(id => id !== product.id));
                          }
                        }}
                        data-testid={`checkbox-product-${product.id}`}
                      />
                      <label
                        htmlFor={`product-${product.id}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {product.productName} ({product.productCode})
                      </label>
                    </div>
                  );
                })}
              </div>
              {selectedProductIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {products
                    .filter(p => selectedProductIds.includes(p.id))
                    .map(product => (
                      <div
                        key={product.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                      >
                        {product.productName}
                        <button
                          type="button"
                          onClick={() => setSelectedProductIds(selectedProductIds.filter(id => id !== product.id))}
                          className="hover:bg-primary/20 rounded-sm p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
              {selectedProductIds.length === 0 && (
                <FormMessage>At least one product must be selected</FormMessage>
              )}
            </FormItem>

            <FormField
              control={form.control}
              name="checklistName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Checklist Name *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Company - Purchase Documents"
                      data-testid="input-checklist-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Standard documents required for company purchase applications..."
                      data-testid="input-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value === 1}
                      onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                      data-testid="checkbox-is-active"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active checklist</FormLabel>
                    <FormDescription>
                      Enable this checklist for use
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending || products.length === 0 || selectedProductIds.length === 0}
                data-testid="button-save"
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"} Checklist
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
